import React from "react"

import { Button } from "@coval/ui"
import type { ChartTable } from "@coval/ui/chart-figure"

import type { ScatterPoint } from "../utils/buildScatterDataset"

interface ScatterFigureOptions {
  /** One entry per simulation, outliers already dropped. */
  points: ScatterPoint[]
  /** One entry per group membership; names every group a simulation is plotted in. */
  memberships: ScatterPoint[]
  xLabel: string
  yLabel: string
  zLabel?: string
  /** True when a Compare By is active, so each row names its group. */
  grouped: boolean
  trendR2?: number
  /** When set, each row opens its simulation, as clicking its point does. */
  onOpen?: (simulationOutputId: string) => void
}

function formatZ(value: number | undefined): string {
  return value == null
    ? "—"
    : value.toLocaleString("en-US", { maximumFractionDigits: 2 })
}

/** The plotted simulations as a sentence and as a table, one row each. */
export function buildScatterFigure({
  points,
  memberships,
  xLabel,
  yLabel,
  zLabel,
  grouped,
  trendR2,
  onOpen,
}: ScatterFigureOptions): { summary: string; table: ChartTable } {
  const count = `${points.length} simulation${points.length === 1 ? "" : "s"} plotted, ${yLabel} against ${xLabel}.`
  const summary =
    trendR2 === undefined
      ? count
      : `${count} Trend line R² = ${trendR2.toFixed(2)}.`

  const groupsBySimulation = new Map<string, string[]>()
  for (const point of memberships) {
    if (!point.groupLabel) {
      continue
    }
    const groups = groupsBySimulation.get(point.simulationOutputId) ?? []
    if (!groups.includes(point.groupLabel)) {
      groups.push(point.groupLabel)
    }
    groupsBySimulation.set(point.simulationOutputId, groups)
  }

  const table: ChartTable = {
    columns: [
      { key: "index", header: "#", numeric: true },
      ...(grouped ? [{ key: "group", header: "Groups" }] : []),
      { key: "x", header: xLabel, numeric: true },
      { key: "y", header: yLabel, numeric: true },
      ...(zLabel ? [{ key: "z", header: zLabel, numeric: true }] : []),
      ...(onOpen ? [{ key: "open", header: "Simulation" }] : []),
    ],
    rows: points.map((point, index) => ({
      key: point.simulationOutputId,
      cells: {
        index: index + 1,
        group:
          groupsBySimulation.get(point.simulationOutputId)?.join(", ") ?? "—",
        x: point.xDisplay,
        y: point.yDisplay,
        z: formatZ(point.z),
        open: onOpen ? (
          <Button
            type="button"
            variant="secondary"
            aria-label={`Open simulation ${index + 1}: ${xLabel} ${point.xDisplay}, ${yLabel} ${point.yDisplay}`}
            onClick={() => onOpen(point.simulationOutputId)}
          >
            Open
          </Button>
        ) : null,
      },
    })),
  }

  return { summary, table }
}
